import { createRouter, createWebHashHistory } from 'vue-router'
import type {
  Router,
  RouteLocationNormalized,
  RouteRecordNormalized,
  RouteRecordRaw
} from 'vue-router'
import { isUrl } from '@/utils/is'
import { omit, cloneDeep } from 'lodash-es'

const modules = import.meta.glob('../views/**/*.{vue,tsx}')

/* Layout */
export const Layout = () => import('@/layout/Layout.vue')

// 二级目录（后端菜单 component 为 "##"）的占位布局组件，内部只渲染 <router-view />
// 说明：这里必须使用「懒加载函数」而不是组件对象。
// 路由表会被 cloneDeep 深拷贝（如静态路由模式 cloneDeep(asyncRouterMap)）并存入 Pinia 的响应式 state，
// 组件对象被深拷贝后会丢失 markRaw 标记，进而被 Vue 响应式系统包装，触发
// "Vue received a Component that was made a reactive object" 警告；
// 而函数既不会被 reactive 包装，也不会被 cloneDeep 深拷贝（lodash 会保留函数引用）
const ParentLayout = () => import('@/layout/components/ParentLayout.vue')

export const getParentLayout = () => ParentLayout

export const getRawRoute = (route: RouteLocationNormalized): RouteLocationNormalized => {
  if (!route) return route
  const { matched, ...opt } = route
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path
        }))
      : undefined) as RouteRecordNormalized[]
  }
}

// 前端控制路由生成
export const generateRoutesByFrontEnd = (
  routes: AppRouteRecordRaw[],
  keys: string[],
  basePath = '/'
): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const route of routes) {
    const meta = route.meta ?? {}
    // skip some route
    if (meta.hidden && !meta.canTo) {
      continue
    }

    let data: Nullable<AppRouteRecordRaw> = null

    let onlyOneChild: Nullable<string> = null
    if (route.children && route.children.length === 1 && !meta.alwaysShow) {
      onlyOneChild = (
        isUrl(route.children[0].path)
          ? route.children[0].path
          : pathResolve(pathResolve(basePath, route.path), route.children[0].path)
      ) as string
    }

    // 开发者可以根据实际情况进行扩展
    for (const item of keys) {
      // 通过路径去匹配
      if (isUrl(item) && (onlyOneChild === item || route.path === item)) {
        data = Object.assign({}, route)
      } else {
        const routePath = (onlyOneChild ?? pathResolve(basePath, route.path)).trim()
        if (routePath === item || meta.followRoute === item) {
          data = Object.assign({}, route)
        }
      }
    }

    // recursive child routes
    if (route.children && data) {
      data.children = generateRoutesByFrontEnd(
        route.children,
        keys,
        pathResolve(basePath, data.path)
      )
    }
    if (data) {
      res.push(data as AppRouteRecordRaw)
    }
  }
  return res
}

// 后端控制路由生成
export const generateRoutesByServer = (routes: AppCustomRouteRecordRaw[]): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const route of routes) {
    const r = route as Record<string, any>
    const meta: Record<string, any> = { ...(route.meta || {}) }
    if (r.hidden !== undefined) meta.hidden = r.hidden
    if (r.alwaysShow !== undefined) meta.alwaysShow = r.alwaysShow
    if (r.icon !== undefined) meta.icon = r.icon
    if (r.noCache !== undefined) meta.noCache = r.noCache
    if (r.affix !== undefined) meta.affix = r.affix
    // 兼容后端（若依风格）将菜单标题置于顶层 menuName / title，而非 meta.title 的情况
    if (meta.title === undefined) {
      if (r.title !== undefined) meta.title = r.title
      else if (r.menuName !== undefined) meta.title = r.menuName
    }

    const data: AppRouteRecordRaw = {
      path: route.path,
      name: route.name,
      meta: meta as any
    }
    if (route.redirect && String(route.redirect).toLowerCase() !== 'noredirect') {
      data.redirect = route.redirect
    }
    if (route.component) {
      const component = route.component as string
      const normalizedComponent = component.replace(/^@\//, '').replace(/^src\//, '')

      const comModule =
        modules[`../${normalizedComponent}.vue`] || modules[`../${normalizedComponent}.tsx`]
      if (!comModule && !normalizedComponent.includes('#')) {
        console.error(`未找到${route.component}.vue文件或${route.component}.tsx文件，请创建`)
      } else {
        data.component =
          normalizedComponent === '#'
            ? Layout
            : normalizedComponent.includes('##')
              ? getParentLayout()
              : comModule
      }
    }
    if (route.children) {
      data.children = generateRoutesByServer(route.children)
      if (!data.redirect && data.children.length > 0) {
        data.redirect = pathResolve(route.path as string, data.children[0].path as string)
      }
    }
    res.push(data as AppRouteRecordRaw)
  }
  return res
}

export const pathResolve = (parentPath: string, path: string) => {
  if (isUrl(path)) return path
  const childPath = path.startsWith('/') || !path ? path : `/${path}`
  return `${parentPath}${childPath}`.replace(/\/\//g, '/').trim()
}

// 路由降级
export const flatMultiLevelRoutes = (routes: AppRouteRecordRaw[]) => {
  const modules: AppRouteRecordRaw[] = cloneDeep(routes)
  for (let index = 0; index < modules.length; index++) {
    const route = modules[index]
    if (!isMultipleRoute(route)) {
      continue
    }
    promoteRouteLevel(route)
  }
  return modules
}

// 层级是否大于2
const isMultipleRoute = (route: AppRouteRecordRaw) => {
  if (!route || !Reflect.has(route, 'children') || !route.children?.length) {
    return false
  }

  const children = route.children

  let flag = false
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    if (child.children?.length) {
      flag = true
      break
    }
  }
  return flag
}

// 生成二级路由
const promoteRouteLevel = (route: AppRouteRecordRaw) => {
  let router: Router | null = createRouter({
    routes: [route as RouteRecordRaw],
    history: createWebHashHistory()
  })

  const routes = router.getRoutes()
  addToChildren(routes, route.children || [], route)
  router = null

  route.children = route.children?.map((item) => omit(item, 'children'))
}

// 添加所有子菜单
const addToChildren = (
  routes: RouteRecordNormalized[],
  children: AppRouteRecordRaw[],
  routeModule: AppRouteRecordRaw
) => {
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    const route = routes.find((item) => item.name === child.name)
    if (!route) {
      continue
    }
    routeModule.children = routeModule.children || []
    if (!routeModule.children.find((item) => item.name === route.name)) {
      routeModule.children?.push(route as unknown as AppRouteRecordRaw)
    }
    if (child.children?.length) {
      addToChildren(routes, child.children, routeModule)
    }
  }
}
